import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  Checkbox,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import SuiBox from "components/SuiBox";
import SuiButton from "components/SuiButton";
import SuiInput from "components/SuiInput";
import SuiTypography from "components/SuiTypography";
import {
  TIPOS_DOCUMENTO,
  TIPOS_PARENTESCO,
  ESTADOS_PROCESO,
  validateEmail,
  validateDocumento,
  validateEdad,
  validateRadicado,
} from "../../utils/adicionalesUtils";

const INITIAL_FORM_STATE = {
  tipo_adicional: "",
  // Campos para ambos: Persona a cargo y Sociedades
  nombre_completo: "",
  tipo_documento: "",
  numero_documento: "",
  // Campos para Personas a cargo
  parentesco: "",
  edad: "",
  // Campos para Sociedades
  direccion_notificacion: "",
  // Campos para Procesos Judiciales
  juzgado: "",
  email: "",
  demandante: "",
  demandado: "",
  radicado: "",
  estado_actual: "",
  mostrar_nota: false,
};

function NuevoAdicionalForm({ open, onClose, onSubmit, procesoId, procesoData = {} }) {
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [errors, setErrors] = useState({});

  // Genera opciones de tipo adicional basadas en los datos de proceso
  const tiposDisponibles = [];
  if (procesoData.personas_a_cargo)
    tiposDisponibles.push({ value: "PERSONA_CARGO", label: "Persona a Cargo" });
  if (procesoData.sociedades)
    tiposDisponibles.push({
      value: "SOCIEDADES_PROCESO",
      label: "Sociedad (Conyugal o Patrimonial)",
    });
  if (procesoData.procesos_judiciales)
    tiposDisponibles.push({ value: "PROCESO_JUDICIAL", label: "Proceso Judicial" });

  const handleTipoAdicionalChange = (event) => {
    const tipoAdicional = event.target.value;
    setFormData({
      ...INITIAL_FORM_STATE,
      tipo_adicional: tipoAdicional,
      // Si es proceso judicial, prellenar el demandado con el nombre del cliente
      ...(tipoAdicional === "PROCESO_JUDICIAL" && procesoData.cliente_detalle
        ? {
            demandado:
              `${procesoData.cliente_detalle.nombres} ${procesoData.cliente_detalle.apellidos}`.trim(),
          }
        : {}),
    });
    setErrors({});
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;

    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));

    if (field === "radicado") {
      const soloDigitos = value.replace(/\D/g, ""); // elimina guiones, espacios, letras, etc.
      if (soloDigitos.length !== 23) {
        setErrors((prev) => ({
          ...prev,
          radicado: "El radicado debe tener exactamente 23 dígitos",
        }));
      } else {
        setErrors((prev) => ({ ...prev, radicado: null }));
      }
    } else if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Validaciones específicas según el tipo
    if (formData.tipo_adicional === "PERSONA_CARGO") {
      if (!formData.nombre_completo) newErrors.nombre_completo = "Campo requerido";
      if (!validateDocumento(formData.numero_documento, formData.tipo_documento)) {
        newErrors.numero_documento = "Documento inválido";
      }
      if (!validateEdad(formData.edad)) newErrors.edad = "Edad inválida";
      if (!formData.parentesco) newErrors.parentesco = "Campo requerido";
    }

    if (formData.tipo_adicional === "SOCIEDADES_PROCESO") {
      if (!formData.tipo_documento) newErrors.tipo_documento = "Campo requerido";
      if (!formData.numero_documento) newErrors.numero_documento = "Campo requerido";
      if (!validateDocumento(formData.numero_documento, formData.tipo_documento)) {
        newErrors.numero_documento = "Documento inválido";
      }
      if (!formData.nombre_completo) newErrors.nombre_completo = "Campo requerido";
      if (!formData.direccion_notificacion) newErrors.direccion_notificacion = "Campo requerido";
    }

    if (formData.tipo_adicional === "PROCESO_JUDICIAL") {
      if (!formData.juzgado) newErrors.juzgado = "Campo requerido";
      if (!validateEmail(formData.email)) newErrors.email = "Correo inválido";
      if (!formData.demandante) newErrors.demandante = "Campo requerido";
      if (!formData.demandado) newErrors.demandado = "Campo requerido";
      if (!validateRadicado(formData.radicado)) newErrors.radicado = "Formato inválido";
      if (!formData.estado_actual) newErrors.estado_actual = "Campo requerido";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!procesoId || !formData.tipo_adicional) return;
    if (!validateForm()) return;
    const adicionalData = {
      proceso: procesoId,
      ...formData,
    };
    onSubmit(adicionalData);
    setFormData(INITIAL_FORM_STATE);
    setErrors({});
    onClose();
  };

  const handleClose = () => {
    setFormData(INITIAL_FORM_STATE);
    setErrors({});
    onClose();
  };

  const renderPersonaCargoFields = () => (
    <>
      <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
        <Grid size={4}>
          <SuiBox mb={2}>
            <SuiTypography variant="h6" fontWeight="dark">
              Tipo Documento
            </SuiTypography>
            <Select value={formData.tipo_documento} onChange={handleChange("tipo_documento")}>
              {TIPOS_DOCUMENTO.map((tipo) => (
                <MenuItem key={tipo.value} value={tipo.value}>
                  {tipo.label}
                </MenuItem>
              ))}
            </Select>
          </SuiBox>
        </Grid>
        <Grid size={4}>
          <SuiBox mb={2}>
            <SuiTypography variant="h6" fontWeight="dark">
              Identificación
            </SuiTypography>
            <SuiInput
              error={!!errors.numero_documento}
              helperText={errors.numero_documento}
              value={formData.numero_documento}
              onChange={handleChange("numero_documento")}
            />
          </SuiBox>
        </Grid>
        <Grid size={4}>
          <SuiBox mb={2}>
            <SuiTypography variant="h6" fontWeight="dark">
              Edad
            </SuiTypography>
            <SuiInput
              error={!!errors.edad}
              helperText={errors.edad}
              value={formData.edad}
              onChange={handleChange("edad")}
              type="number"
            />
          </SuiBox>
        </Grid>
      </Grid>
      <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
        <Grid size={6}>
          <SuiBox mb={2}>
            <SuiTypography variant="h6" fontWeight="dark">
              Nombre Completo
            </SuiTypography>
            <SuiInput
              error={!!errors.nombre_completo}
              helperText={errors.nombre_completo}
              value={formData.nombre_completo}
              onChange={handleChange("nombre_completo")}
            />
          </SuiBox>
        </Grid>
        <Grid size={6}>
          <SuiBox mb={2}>
            <SuiTypography variant="h6" fontWeight="dark">
              Parentesco
            </SuiTypography>
            <Select value={formData.parentesco} onChange={handleChange("parentesco")}>
              {TIPOS_PARENTESCO.map((tipo) => (
                <MenuItem key={tipo.value} value={tipo.value}>
                  {tipo.label}
                </MenuItem>
              ))}
            </Select>
          </SuiBox>
        </Grid>
      </Grid>
    </>
  );

  const renderSociedadesProcesoFields = () => (
    <>
      <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
        <Grid size={4}>
          <SuiBox mb={2}>
            <SuiTypography variant="h6" fontWeight="dark">
              Tipo Sociedad
            </SuiTypography>
            <Select
              value={formData.tipo_sociedad}
              onChange={handleChange("tipo_sociedad")}
              sx={{ opacity: 0.8 }}
            >
              <MenuItem value="" disabled>
                <em>Tipo Sociedad</em>
              </MenuItem>
              <MenuItem value="CONYUGAL">CONYUGAL</MenuItem>
              <MenuItem value="PATRIMONIAL">PATRIMONIAL</MenuItem>
            </Select>
          </SuiBox>
        </Grid>
        <Grid size={4}>
          <SuiBox mb={2}>
            <SuiTypography variant="h6" fontWeight="dark">
              Tipo Documento
            </SuiTypography>
            <Select value={formData.tipo_documento} onChange={handleChange("tipo_documento")}>
              {TIPOS_DOCUMENTO.map((tipo) => (
                <MenuItem key={tipo.value} value={tipo.value}>
                  {tipo.label}
                </MenuItem>
              ))}
            </Select>
          </SuiBox>
        </Grid>
        <Grid size={4}>
          <SuiBox mb={2}>
            <SuiTypography variant="h6" fontWeight="dark">
              Identificación
            </SuiTypography>
            <SuiInput
              error={!!errors.numero_documento}
              helperText={errors.numero_documento}
              value={formData.numero_documento}
              onChange={handleChange("numero_documento")}
            />
          </SuiBox>
        </Grid>
      </Grid>
      <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
        <Grid size={6}>
          <SuiBox mb={2}>
            <SuiTypography variant="h6" fontWeight="dark">
              Nombre
            </SuiTypography>
            <SuiInput
              error={!!errors.nombre_completo}
              helperText={errors.nombre_completo}
              value={formData.nombre_completo}
              onChange={handleChange("nombre_completo")}
            />
          </SuiBox>
        </Grid>
        <Grid size={6}>
          <SuiBox mb={2}>
            <SuiTypography variant="h6" fontWeight="dark">
              Dirección Notificación
            </SuiTypography>
            <SuiInput
              error={!!errors.direccion_notificacion}
              helperText={errors.direccion_notificacion}
              value={formData.direccion_notificacion}
              onChange={handleChange("direccion_notificacion")}
            />
          </SuiBox>
        </Grid>
      </Grid>
    </>
  );

  const renderProcesoJudicialFields = () => (
    <>
      <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
        <Grid size={6}>
          <SuiBox mb={2}>
            <SuiTypography variant="h6" fontWeight="dark">
              Juzgado
            </SuiTypography>
            <SuiInput
              error={!!errors.juzgado}
              helperText={errors.juzgado}
              value={formData.juzgado}
              onChange={handleChange("juzgado")}
            />
          </SuiBox>
        </Grid>
        <Grid size={6}>
          <SuiBox mb={2}>
            <SuiTypography variant="h6" fontWeight="dark">
              Correo Electrónico
            </SuiTypography>
            <SuiInput
              error={!!errors.email}
              helperText={errors.email}
              value={formData.email}
              onChange={handleChange("email")}
            />
          </SuiBox>
        </Grid>
      </Grid>
      <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
        <Grid size={6}>
          <SuiBox mb={2}>
            <SuiTypography variant="h6" fontWeight="dark">
              Demandante
            </SuiTypography>
            <SuiInput
              error={!!errors.demandante}
              helperText={errors.demandante}
              value={formData.demandante}
              onChange={handleChange("demandante")}
            />
          </SuiBox>
        </Grid>
        <Grid size={6}>
          <SuiBox mb={2}>
            <SuiTypography variant="h6" fontWeight="dark">
              Demandado
            </SuiTypography>
            <SuiInput
              error={!!errors.demandado}
              helperText={errors.demandado}
              value={formData.demandado}
              onChange={handleChange("demandado")}
              placeholder={
                procesoData.cliente_detalle
                  ? `${procesoData.cliente_detalle.nombres} ${procesoData.cliente_detalle.apellidos}`.trim()
                  : "Nombre del demandado"
              }
            />
          </SuiBox>
        </Grid>
      </Grid>
      <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
        <Grid size={12}>
          <SuiBox mb={2}>
            <SuiTypography variant="h6" fontWeight="dark">
              Radicado{" "}
              <span style={{ fontSize: "12px", color: "#889fccff", minWidth: "35px" }}>
                (debe tener 23 caracteres, sin contar los guiones)
              </span>
            </SuiTypography>
            <SuiInput
              error={!!errors.radicado}
              helperText={errors.radicado}
              value={formData.radicado}
              onChange={handleChange("radicado")}
            />
          </SuiBox>
        </Grid>
      </Grid>
      <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
        <Grid size={6}>
          <SuiBox mb={2}>
            <SuiTypography variant="h6" fontWeight="dark">
              Estado Actual
            </SuiTypography>
            <Select value={formData.estado_actual} onChange={handleChange("estado_actual")}>
              {ESTADOS_PROCESO.map((estado) => (
                <MenuItem key={estado.value} value={estado.value}>
                  {estado.label}
                </MenuItem>
              ))}
            </Select>
          </SuiBox>
        </Grid>
        <Grid item size={6}>
          <SuiTypography variant="h6" fontWeight="dark" sx={{ mt: 2 }}>
            <Checkbox
              checked={formData.mostrar_nota}
              onChange={(e) => {
                setFormData((prev) => ({
                  ...prev,
                  mostrar_nota: e.target.checked,
                }));
              }}
            />
            {"  "}
            ¿Incluir la Nota: Se deja constancia de que a la fecha de...?
          </SuiTypography>
        </Grid>
      </Grid>
    </>
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <SuiTypography variant="h6">Nuevo Adicional</SuiTypography>
      </DialogTitle>
      <DialogContent>
        <SuiBox py={1}>
          <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={6}>
              <SuiBox mb={2}>
                <SuiTypography variant="h6" fontWeight="dark">
                  Tipo Adicional
                </SuiTypography>
                <Select value={formData.tipo_adicional} onChange={handleTipoAdicionalChange}>
                  {tiposDisponibles.map((tipo) => (
                    <MenuItem key={tipo.value} value={tipo.value}>
                      {tipo.label}
                    </MenuItem>
                  ))}
                </Select>
              </SuiBox>
            </Grid>
          </Grid>
          {formData.tipo_adicional === "PERSONA_CARGO" && renderPersonaCargoFields()}
          {formData.tipo_adicional === "SOCIEDADES_PROCESO" && renderSociedadesProcesoFields()}
          {formData.tipo_adicional === "PROCESO_JUDICIAL" && renderProcesoJudicialFields()}
        </SuiBox>
      </DialogContent>
      <DialogActions>
        <SuiButton onClick={handleClose} buttonColor="light">
          Cancelar
        </SuiButton>
        <SuiButton onClick={handleSubmit} variant="gradient" buttonColor="dark">
          Agregar Adicional
        </SuiButton>
      </DialogActions>
    </Dialog>
  );
}

NuevoAdicionalForm.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  procesoId: PropTypes.number.isRequired,
  procesoData: PropTypes.shape({
    personas_a_cargo: PropTypes.bool,
    sociedades: PropTypes.bool,
    procesos_judiciales: PropTypes.bool,
  }),
};

NuevoAdicionalForm.defaultProps = {
  procesoData: {
    personas_a_cargo: false,
    sociedades: false,
    procesos_judiciales: false,
  },
};

export default NuevoAdicionalForm;
